/**
 * Jira Integration Query Hooks
 */

import { useNavigate } from 'react-router-dom';
import { addToast, Button } from '@heroui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CloneToProjectRequest,
  CloneToProjectResponse,
  JiraCompleteConnectionResponse,
  JiraIssuesParams,
  JiraIssuesResponse,
  JiraStatusResponse,
} from '~/services/api/jira';
import * as jiraApi from '~/services/api/jira';
import * as projectsApi from '~/services/api/projects';
import { usePRDStore } from '~/stores/prdStore';
import { useUIStore } from '~/stores/uiStore';

/**
 * Query key factory for Jira.
 */
export const jiraKeys = {
  all: ['jira'] as const,
  status: () => [...jiraKeys.all, 'status'] as const,
  issues: (params: JiraIssuesParams = {}) => [...jiraKeys.all, 'issues', params] as const,
};

/**
 * Clone a Jira issue to a new project.
 * Creates project with LLM-generated title/description.
 *
 * Hook-level callbacks handle: spinner state, toast notifications, cache invalidation.
 * These always fire even if component unmounts.
 *
 * Component-level callbacks (passed to mutate()) handle: navigation, modal close.
 * These only fire if component is still mounted.
 */
export function useCloneToProject(): ReturnType<
  typeof useMutation<CloneToProjectResponse, Error, CloneToProjectRequest>
> {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setActiveSession } = usePRDStore();
  const { setActiveProjectAssistantTab, setCloningJiraIssueKey, setProjectAssistantExpanded } =
    useUIStore();

  return useMutation({
    mutationFn: (request: CloneToProjectRequest) => jiraApi.postCloneToProject(request),

    onMutate: variables => {
      setCloningJiraIssueKey(variables.issue_key);
    },

    onSuccess: async result => {
      // Prefetch both the specific project AND refetch the projects list
      // Both must complete before navigation to avoid showing stale data
      await Promise.all([
        // Prefetch the specific project for the detail page
        queryClient.prefetchQuery({
          queryKey: ['project', result.project_id],
          queryFn: () => projectsApi.getProject(result.project_id),
        }),
        // Refetch projects list for sidebar (refetchQueries waits for completion)
        queryClient.refetchQueries({ queryKey: ['projects'] }),
      ]);

      // Always show toast with action button (fires even if component unmounted)
      addToast({
        color: 'success',
        description: result.prd_session_id
          ? 'Project created! PRD generation started.'
          : 'Project created.',
        endContent: (
          <Button
            color="primary"
            size="sm"
            variant="flat"
            onPress={() => {
              // Set up PRD sidebar state before navigating
              if (result.prd_session_id) {
                setActiveSession(result.project_id, result.prd_session_id);
                setProjectAssistantExpanded(true);
                setActiveProjectAssistantTab('chat');
              }
              void navigate(result.redirect_url);
            }}
          >
            View Project
          </Button>
        ),
        timeout: 8000,
      });
    },

    onError: (error: Error) => {
      const errorMessage = error.message || '';
      const isDuplicate =
        errorMessage.includes('duplicate') || errorMessage.includes('already exists');

      addToast({
        color: isDuplicate ? 'warning' : 'danger',
        description: isDuplicate
          ? 'A project already exists for this Jira issue'
          : errorMessage || 'Failed to clone Jira issue',
        timeout: 5000,
      });
    },

    onSettled: () => {
      setCloningJiraIssueKey(null);
    },
  });
}

/**
 * Complete Jira connection after multi-site selection.
 */
export function useJiraCompleteConnection(): ReturnType<
  typeof useMutation<
    JiraCompleteConnectionResponse,
    Error,
    { pendingState: string; siteId: string }
  >
> {
  return useMutation({
    mutationFn: ({ pendingState, siteId }) =>
      jiraApi.postJiraCompleteConnection(pendingState, siteId),
  });
}

/**
 * Initiate Jira OAuth connection flow.
 * Returns redirect URL to Atlassian.
 */
export function useJiraConnect(): ReturnType<
  typeof useMutation<{ redirect_url: string }, Error, void>
> {
  return useMutation({
    mutationFn: () => jiraApi.postJiraConnect(),
  });
}

/**
 * Disconnect Jira integration.
 * Invalidates status query on success.
 */
export function useJiraDisconnect(): ReturnType<typeof useMutation<void, Error, void>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => jiraApi.deleteJiraConnection(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jiraKeys.status() });
    },
  });
}

/**
 * Fetch Jira issues assigned to the current user.
 * Only fetches when user has a connected Jira account.
 *
 * @param params - Pagination and sort parameters
 */
export function useJiraIssues(params: JiraIssuesParams = {}): {
  data: JiraIssuesResponse | undefined;
  error: unknown;
  isLoading: boolean;
  refetch: () => void;
} {
  const { status } = useJiraStatus();
  const isConnected = status?.connected ?? false;

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: jiraKeys.issues(params),
    queryFn: () => jiraApi.getJiraIssues(params),
    enabled: isConnected,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    data,
    error,
    isLoading,
    refetch: (): void => {
      void refetch();
    },
  };
}

/**
 * Fetch Jira connection status for current user.
 */
export function useJiraStatus(): {
  error: unknown;
  isLoading: boolean;
  refetch: () => void;
  status: JiraStatusResponse | undefined;
} {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: jiraKeys.status(),
    queryFn: () => jiraApi.getJiraStatus(),
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    error,
    isLoading,
    refetch: (): void => {
      void refetch();
    },
    status: data,
  };
}
